import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { calculateHasBeenInView, calculateTrackingInView, handleSkipInView } from 'components/utils/OfferInView'
import config from 'constants/config'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import useExperience from 'hooks/Experiences/useExperience'
import useToggle from 'hooks/useToggle'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import ExperienceLocationCaption from './ExperienceLocationCaption'
import ExperienceOfferPriceDetails from './ExperienceOfferPriceDetails'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import ExperienceUrgencyTag from 'components/Experiences/ExperienceTiles/ExperienceUrgencyTag'

const Card = styled.div`
  display: grid;
  width: 75vw;
  height: 100%;
  flex-shrink: 0;

  > * {
    grid-column-start: 1;
    grid-row-start: 1;
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(320)};
  }

  ${mediaQueryUp.desktop} {
    &.size-medium {
      width: ${rem(512)};
    }
  }
`

const Loader = styled(OfferCardLoadingSkeleton)`
  opacity: 0;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
  }
`

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  id: string
  index?: number
  lazyLoad?: boolean
  className?: string
  size?: 'small' | 'medium'
  onClick?: (experience?: App.ExperienceOffer) => void
  listKey?: string
}

function ExperienceOfferCard(props: Props) {
  const { id, index, lazyLoad, className, size = 'small', onClick, variant, eagerLoadImage, listKey } = props
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [skipInView, setSkipInView] = useState(false)
  const [inViewRef, inView, entry] = useInView({
    rootMargin: '100px',
    threshold: [0, OFFER_TRACKING_IN_VIEW_THRESHOLD],
    skip: skipInView,
  })

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, inView])

  const hasBeenInView = useMemo(() => calculateHasBeenInView(entry), [entry])

  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  const [experienceRaw] = useExperience(id, { disabled: lazyLoad && !hasBeenInView })
  const [loader,,, hideLoader] = useToggle(!!experienceRaw)

  const experience = useMemo(() => {
    return experienceRaw ? mapExperienceToBookingView(experienceRaw) : undefined
  }, [experienceRaw])

  useEffect(() => {
    if (trackingInView && experienceRaw && typeof index !== 'undefined') {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: experienceRaw,
        position: index,
        key: EventDataKey.ExperienceImpression,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- Only
  }, [trackingInView, experienceRaw])

  const onOfferClick = useCallback(() => {
    if (onClick) {
      onClick(experienceRaw)
    } else if (experienceRaw && typeof index !== 'undefined') {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experienceRaw,
        position: index,
        key: EventDataKey.ExperienceClick,
      })
    }
  }, [experienceRaw, index, dispatchOfferListEvent, onClick])

  const pauseLoader = !hasBeenInView || !!experience

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(experienceRaw, {}),
  ], [experienceRaw])

  const offersMetadata = useAppSelector(state => listKey ? state.experience.experienceOffersMetadata[listKey] : undefined)
  const urgencyTags = useMemo(() => offersMetadata?.[id]?.urgencyTags ?? [], [offersMetadata, id])

  return (
    <ProductPaletteProvider palette="default">
      <Card ref={inViewRef} className={`size-${size}`} data-testid="offer-card">
        <LoaderPlayStateContext.Provider value={pauseLoader ? 'paused' : 'running'}>
          {loader && <Loader
            size={size}
            onTransitionEnd={hideLoader}
            className={!experience ? 'visible' : undefined}
          />}
          {experience && hasBeenInView && <>
            {size === 'medium' && <CarouselCardMedium
              variant={variant}
              className={className}
              bookmarkButton={<ExperienceBookmarkButton offer={experience.originalExperience} withLabel="tablet" />}
              image={experience.image!}
              eagerLoadImage={eagerLoadImage}
              providerName={experience.originalExperience.vendor?.name}
              location={<ExperienceLocationCaption location={experience.location} />}
              title={experience.title}
              priceDetails={<Group direction="vertical" gap={4}>
                <AgentHubAdditionalCommissionLabel offerId={experienceRaw?.id} />
                {experience.originalExperience.campaignTags?.map(campaignTag => <SiteTakeoverCampaignTagLabel key={campaignTag} campaignTagId={campaignTag} onOfferCard />)}
                <ExperienceOfferPriceDetails experience={experience} variant="condensed" />
              </Group>}
              to={getExperienceOfferPageURL(experience.originalExperience)}
              rating={config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) ? experience.rating : undefined}
              onClick={onOfferClick}
              loyaltyPointsElement={<LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
                offer={experience.originalExperience}
              />}
              imageCornerLabel={<ExperienceUrgencyTag urgencyTags={urgencyTags} experience={experience.originalExperience} />}
            />}
            {size === 'small' && <CarouselCardSmall
              variant={variant}
              className={className}
              bookmarkButton={<ExperienceBookmarkButton offer={experience.originalExperience} withLabel="tablet" />}
              description={experience.description}
              image={experience.image!}
              eagerLoadImage={eagerLoadImage}
              location={<ExperienceLocationCaption location={experience.location} />}
              title={experience.title}
              priceDetails={<Group direction="vertical" gap={2}>
                <AgentHubAdditionalCommissionLabel offerId={experienceRaw?.id} />
                {experience.originalExperience.campaignTags?.map(campaignTag => <SiteTakeoverCampaignTagLabel key={campaignTag} campaignTagId={campaignTag} onOfferCard />)}
                <ExperienceOfferPriceDetails experience={experience} />
              </Group>}
              to={getExperienceOfferPageURL(experience.originalExperience)}
              rating={config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) ? experience.rating : undefined}
              onClick={onOfferClick}
              loyaltyPointsElement={<LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
                offer={experience.originalExperience}
              />}
              imageCornerLabel={<ExperienceUrgencyTag urgencyTags={urgencyTags} experience={experience.originalExperience} />}
            />}
          </>}
        </LoaderPlayStateContext.Provider>
      </Card>
    </ProductPaletteProvider>
  )
}

export default ExperienceOfferCard
